/**
 * IPLogger - Tracks player progression through game stages
 *
 * Logs transitions to the server API which records:
 * - Player IP address
 * - Game stage reached
 * - Time spent at each stage (calculated from timestamps)
 *
 * Log format on server: "192.168.1.1, main_screen (00:05)"
 */

// API base URL - in production, use absolute path; in dev, use relative (Vite proxy)
const API_BASE = import.meta.env.PROD ? '' : '';

/**
 * Valid stage names for logging
 * @typedef {'main_screen'|'level_1_cutscene'|'level_1_start'|'level_1_death'|'level_2_cutscene'|'level_2_start'|'level_2_death'|'credits_start'} StageType
 */

/**
 * Log a player reaching a game stage
 * @param {StageType} stage - The stage name to log
 * @returns {Promise<void>}
 */
export async function logStage(stage) {
  try {
    const response = await fetch(`${API_BASE}/api/iplog/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stage }),
    });

    if (!response.ok) {
      console.warn(`[IPLogger] Failed to log stage: ${stage}`, response.status);
    }
  } catch (error) {
    // Silently fail - don't interrupt gameplay for logging failures
    console.warn(`[IPLogger] Error logging stage: ${stage}`, error.message);
  }
}

// Export stage constants for type safety
export const STAGES = {
  MAIN_SCREEN: 'main_screen',
  LEVEL_1_CUTSCENE: 'level_1_cutscene',
  LEVEL_1_START: 'level_1_start',
  LEVEL_1_DEATH: 'level_1_death',
  LEVEL_2_CUTSCENE: 'level_2_cutscene',
  LEVEL_2_START: 'level_2_start',
  LEVEL_2_DEATH: 'level_2_death',
  CREDITS_START: 'credits_start',
};

export default { logStage, STAGES };
